import { useState } from "react";

import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  useTheme
} from "@mui/material";
import FactoryIcon from "@mui/icons-material/Factory";

import { hexToRgba } from "@aclymatepackages/converters";
import { truncateText } from "@aclymatepackages/other-helpers";

import SlideLayout from "../../../../../layouts/SlideLayout";

import OpenAiVendorCategorization from "../../../../../inputs/vendors/OpenAiVendorCategorization";

const NaicsReviewSlider = ({ vendor, onClose, onUpdate, onDismiss }) => {
  const { palette } = useTheme();
  const {
    relationshipId,
    vendorId,
    name,
    vendorName,
    naicsTitle,
    emissionCategory,
    scopeThreeCategory,
    confidenceScore,
    transactionCount
  } = vendor;

  const [selectedSuggestion, setSelectedSuggestion] = useState(null);

  const editVendor = (field) => (value) =>
    setSelectedSuggestion((current) => ({
      ...(current || {}),
      [field]: value
    }));

  const canConfirm =
    !!selectedSuggestion &&
    !!selectedSuggestion.naicsCode &&
    !!selectedSuggestion.naicsTitle;

  const onConfirmUpdate = () => {
    if (!canConfirm) {
      return;
    }
    onUpdate({
      relationshipId,
      vendorId,
      vendorName: vendorName || name,
      emissionCategory,
      scopeThreeCategory,
      naicsCode: selectedSuggestion.naicsCode,
      naicsTitle: selectedSuggestion.naicsTitle,
      tonsCo2ePerDollar: selectedSuggestion.tonsCo2ePerDollar
    });
    onClose();
  };

  const onConfirmDismiss = () => {
    onDismiss(relationshipId);
    onClose();
  };

  return (
    <SlideLayout
      isSlideOpen
      setIsSlideOpen={onClose}
      header={
        <Grid container alignItems="center" spacing={2} wrap="nowrap">
          <Grid item>
            <Box
              p={1.5}
              style={{
                backgroundColor: hexToRgba(palette.primary.main, 0.15),
                borderRadius: 999,
                display: "flex"
              }}
            >
              <FactoryIcon color="primary" />
            </Box>
          </Grid>
          <Grid item xs>
            <Typography variant="h5">{name}</Typography>
            <Typography variant="caption" color="textSecondary">
              {`${transactionCount} transaction${
                transactionCount === 1 ? "" : "s"
              } · low-confidence industry`}
            </Typography>
          </Grid>
        </Grid>
      }
      content={
        <Grid container direction="column" spacing={3}>
          <Grid item>
            <Paper
              variant="outlined"
              style={{
                borderColor: palette.warning.main,
                backgroundColor: hexToRgba(palette.warning.main, 0.08)
              }}
            >
              <Box p={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Current industry
                </Typography>
                <Typography variant="body1">{naicsTitle}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {`Confidence ${Math.round(confidenceScore * 100)}%`}
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item container direction="column" spacing={2}>
            <Grid item>
              <Typography variant="subtitle2">Find a better match</Typography>
              <Typography variant="body2" color="textSecondary">
                Describe what this vendor actually does. Pick a suggestion to
                use it as the new industry for this vendor.
              </Typography>
            </Grid>
            <OpenAiVendorCategorization
              naicsCode={selectedSuggestion?.naicsCode || null}
              naicsTitle={selectedSuggestion?.naicsTitle || null}
              editVendor={editVendor}
              questionText="Describe what this vendor actually does"
            />
          </Grid>
        </Grid>
      }
      footer={
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          wrap="nowrap"
        >
          <Grid item>
            <Button onClick={onConfirmDismiss} color="inherit">
              Looks fine — dismiss
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={!canConfirm}
              onClick={onConfirmUpdate}
            >
              {canConfirm
                ? `Use "${truncateText(selectedSuggestion.naicsTitle, 40)}"`
                : "Pick an industry first"}
            </Button>
          </Grid>
        </Grid>
      }
    />
  );
};

export default NaicsReviewSlider;
